import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup } from './firebase.config';
import {
  Button, Box, Typography, Stack, Chip, Link,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../auth/AuthContext';

// ── Design tokens (match landing / footer) ──────────────────────────────────
const PURPLE = '#7C3AED';
const PURPLE_LIGHT = '#A78BFA';
const PURPLE_DEEP = '#6D28D9';
const INK = '#1A0F2E';
const MUTED = '#6B6480';
const BORDER = '#ECE9F4';
const GRADIENT = 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)';

// Inline Google "G" so the button styling matches the mockup exactly
const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const FEATURES = [
  { label: 'CBAM', title: 'CBAM Reports' },
  { label: 'ENERGY', title: 'Energy Audit' },
  { label: 'EVIDENCE', title: 'Verifier-Ready' },
  { label: 'INSIGHTS', title: 'Cost & Exposure' },
];

const Home = () => {
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();

  // After AuthContext finishes loading the user, navigate to the right place.
  const { isLoggedIn, loading, isSuperAdmin, isAppAdmin, isVerifier } = useAuth();

  useEffect(() => {
  // Clear old tenant login context when opening the normal login page
  sessionStorage.removeItem("activeTenantLoginId");
}, []);

  // useEffect(() => {
  //   if (loading) return;
  //   if (!isLoggedIn) return;

  //   // Decide landing page based on role.
  //   if (isSuperAdmin) {
  //     navigate('/tenant-management');
  //   } else if (isVerifier) {
  //     navigate('/verifier');          // NEW: Verifier lands on placeholder
  //   } else if (isAppAdmin) {
  //     navigate('/audits');
  //   } else {
  //     navigate('/audits');            // AppUser (default)
  //   }
  // }, [loading, isLoggedIn, isSuperAdmin, isVerifier, isAppAdmin, navigate]);

  useEffect(() => {
  if (loading) return;
  if (!isLoggedIn) return;

  if (isSuperAdmin) {
    navigate('/tenant-management');
  } else if (isVerifier) {
    navigate('/verifier');
  } else {
    navigate('/dashboard');   // CHANGED: was '/audits'
  }
}, [loading, isLoggedIn, isSuperAdmin, isVerifier, isAppAdmin, navigate]);

  const handleGoogleSignIn = async () => {
    if (!captchaVerified) {
      alert('Please verify CAPTCHA first.');
      return;
    }
    if (!agreedToTerms) {
      alert('Please accept the Terms and Conditions to continue.');
      return;
    }
    setIsSigningIn(true);
    try {
      // That's it. AuthContext's onAuthStateChanged listener takes over from here:
      //   - It looks up the profile in Firestore (or recognises SuperAdmin from .env).
      //   - It signs out unauthorised users automatically.
      //   - It populates currentUser/role/tenantId.
      // The useEffect above will then navigate the user.
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Sign-in error:', err);

      // User cancellation isn't an error worth showing.
      if (err?.code !== 'auth/popup-closed-by-user') {
        alert('An error occurred during sign-in. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', background: '#FFFFFF' }}>
      {/* ── Main: hero (left) + sign-in card (right) ───────────────────── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 6 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
          gap: { xs: 5, md: 8 },
          alignItems: 'center',
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Left: hero */}
        <Box>
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 1.8, py: 0.7, mb: 3,
              borderRadius: 99,
              backgroundColor: '#F3EFFC',
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: PURPLE }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: PURPLE_DEEP }}>
              COMPLIANCE AUDIT PLATFORM
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{ fontWeight: 800, fontSize: { xs: 44, md: 60 }, lineHeight: 1.05, color: INK, letterSpacing: '-0.02em' }}
          >
            Compliance audits.
          </Typography>
          <Typography
            component="span"
            sx={{
              fontWeight: 800, fontSize: { xs: 44, md: 60 }, lineHeight: 1.05, letterSpacing: '-0.02em',
              background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              display: 'block', mb: 3,
            }}
          >
            One platform.
          </Typography>

          <Typography sx={{ fontSize: 17.5, lineHeight: 1.6, color: '#4B4660', maxWidth: 540, mb: 4 }}>
            A modern platform for the compliance audits that define modern trade — CBAM, Energy Audits,
            and the frameworks to come — built for exporters, sustainability teams, and accredited verifiers.
          </Typography>

          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
            {['EU CBAM Aligned', 'BEE & ISO 50001 Ready', 'Verifier-Ready', 'Enterprise-Grade'].map((chip) => (
              <Chip
                key={chip}
                label={chip}
                sx={{
                  height: 38, px: 0.5, fontSize: 13.5, fontWeight: 600, color: '#4B4660',
                  backgroundColor: '#fff', border: `1px solid ${BORDER}`,
                  '& .MuiChip-label': { px: 1.5 },
                }}
                avatar={<Box sx={{ width: 7, height: 7, borderRadius: '50%', background: PURPLE_LIGHT, ml: 1.5 }} />}
              />
            ))}
          </Stack>
        </Box>

        {/* Right: sign-in card */}
        <Box
          sx={{
            background: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(124,58,237,0.12)',
            p: { xs: 3.5, md: 5 },
          }}
        >
          <Typography sx={{ textAlign: 'center', fontWeight: 800, fontSize: 26, color: INK, mb: 1 }}>
            Sign in to continue
          </Typography>
          <Typography sx={{ textAlign: 'center', fontSize: 15, color: MUTED, mb: 3 }}>
            Welcome back. Access your audit workspace.
          </Typography>

          {/* reCAPTCHA — required gate, kept intact */}
          <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    mb: 3,
    width: "100%",
    "& .grecaptcha-badge": {
      visibility: "hidden",
    },
  }}
>
    <ReCAPTCHA
        sitekey={process.env.REACT_APP_SITE_KEY}
        onChange={onCaptchaChange}
    />
</Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              px: 0.5,
            }}
          >
            <input
              type="checkbox"
              id="agreeTerms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
            />
            <Typography
              component="label"
              htmlFor="agreeTerms"
              sx={{ fontSize: 13.5, color: MUTED, cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}
            >
              I have read and agree to the{' '}
              <Link component={RouterLink} to="/tnc" sx={{ color: PURPLE_DEEP, fontWeight: 600 }} underline="hover">
                Terms and Conditions
              </Link>.
            </Typography>
          </Box>

          <Button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn || !agreedToTerms}
            fullWidth
            disableElevation
            startIcon={<GoogleG />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 16,
              color: INK,
              backgroundColor: '#fff',
              border: `1px solid ${BORDER}`,
              borderRadius: 2.5,
              py: 1.6,
              mb: 3,
              '&:hover': { backgroundColor: '#FAF9FE', borderColor: PURPLE_LIGHT },
            }}
          >
            {isSigningIn ? 'Signing in...' : 'Login'}
          </Button>

          <Typography sx={{ textAlign: 'center', fontSize: 13, color: MUTED, mb: 3 }}>
            By signing in, you agree to our{' '}
            <Link component={RouterLink} to="/tnc" sx={{ color: PURPLE_DEEP, fontWeight: 600 }} underline="hover">
              Terms
            </Link>{' '}
            and{' '}
            <Link component={RouterLink} to="/tnc" sx={{ color: PURPLE_DEEP, fontWeight: 600 }} underline="hover">
              Privacy Policy
            </Link>.
          </Typography>
        </Box>
      </Box>

      {/* ── Feature strip ──────────────────────────────────────────────── */}
      <Box
        sx={{
          borderTop: `1px solid ${BORDER}`,
          px: { xs: 3, md: 6 },
          py: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 3, md: 0 },
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
        }}
      >
        {FEATURES.map((f, i) => (
          <Stack
            key={f.label}
            direction="row"
            spacing={1.8}
            alignItems="center"
            sx={{
              px: { md: 3 },
              borderRight: { md: i < FEATURES.length - 1 ? `1px solid ${BORDER}` : 'none' },
            }}
          >
            <Box
              sx={{
                width: 46, height: 46, borderRadius: 2,
                background: GRADIENT,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                boxShadow: '0 6px 16px rgba(124,58,237,0.25)',
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>
                {f.label[0]}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, color: MUTED }}>
                {f.label}
              </Typography>
              <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: INK }}>
                {f.title}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default Home;